import { useEffect, useState } from 'react';

import styled from 'styled-components';

import { mediumShadow, colors, spacing, fontSizes, fontWeights } from 'ui/theme';
import { ErrorStatePageError } from 'ui/illustrations/ErrorStatePageError';

import { useTranslations } from '../../../hooks/useTranslations';
import { NewImage, PromiseStatus } from '../../../types';
import { EmptyStateImages } from 'ui/illustrations';

const DetailImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.base};
  padding: ${spacing.double};
`;

const CTA = styled.span`
  color: ${({ theme }) => theme.colors.textColors.textSecondary};
  font-size: ${fontSizes.s20};
  font-weight: ${fontWeights.bold};
  text-align: center;
`;

const ErrorStateText = styled.div`
  color: ${colors.textSecondary};
  font-size: ${fontSizes.s24};
  font-weight: ${fontWeights.bold};
  text-align: center;
`;

const StyledSingleImage = styled.div<{ $imgSrc: string }>`
  ${({ $imgSrc }) => ($imgSrc ? `background-image: url(${$imgSrc});` : ``)}
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  height: 100%;
  width: 100%;
`;

const ImageStack = styled.div`
  height: ${({ theme }) => theme.spacing.utils.pxToRem(160)};
  position: relative;
  width: ${({ theme }) => theme.spacing.utils.pxToRem(200)};
`;

const StyledStackableImage = styled.div<{ $imgSrc: string; $position: number }>`
  background-image: ${({ $imgSrc }) => `url(${$imgSrc})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 4px;
  border: ${({ theme }) => `${theme.spacing.values.quarter} solid #fff`};
  height: ${({ theme }) => theme.spacing.utils.pxToRem(128)};
  left: 50%;
  position: absolute;
  top: 50%;
  ${({ $position }) => {
    switch ($position) {
      case 3:
        return `transform: rotate(10deg) translate(-35%, -53%);`;
      case 2:
        return `transform: rotate(-10deg) translate(-65%, -53%);`;
      default:
        // 1
        return `transform: translate(-50%, -50%);`;
    }
  }}
  transform-origin: top left; /** normally 'center', but 'top left' due to absolute positioning */
  width: ${({ theme }) => theme.spacing.utils.pxToRem(128)};
  ${mediumShadow};
`;

const ImageStackLbl = styled.span`
  color: ${({ theme }) => theme.colors.textColors.textSecondary};
  font-size: 22px;
  font-weight: bold;
  line-height: 1.2;
  text-align: center;
`;

export const testIds = {
  imageMultiple: 'cl-index-takeover-detail-image-multiple',
  imageNone: 'cl-index-takeover-detail-image-none',
  imageSingle: 'cl-index-takeover-detail-image-single',
  errorState: 'cl-index-takeover-detail-image-error',
};

export const SingleImageFile = ({ file }: { file: File }) => {
  const [dataURL, setDataURL] = useState<string>('');

  useEffect(() => {
    const imgURL = URL.createObjectURL(file);
    setDataURL(imgURL);
    return () => {
      URL.revokeObjectURL(imgURL);
    };
  }, [file]);

  return <StyledSingleImage $imgSrc={dataURL} data-test={testIds.imageSingle} />;
};

export const StackableImageFile = ({ file, position }: { file: File; position: number }) => {
  const [dataURL, setDataURL] = useState<string>('');

  useEffect(() => {
    const imgURL = URL.createObjectURL(file);
    setDataURL(imgURL);
    return () => {
      URL.revokeObjectURL(imgURL);
    };
  }, [file]);

  return (
    <StyledStackableImage
      $imgSrc={dataURL}
      $position={position}
      data-test={testIds.imageMultiple}
    />
  );
};

export const StackableImage = ({ asset, position }: { asset: NewImage; position: number }) =>
  asset.file.file ? (
    <StackableImageFile position={position} file={asset.file.file} />
  ) : (
    <StyledStackableImage
      $imgSrc={asset.asset?.url || ''}
      $position={position}
      data-test={testIds.imageMultiple}
    />
  );

export const TakeoverDetailImage = ({ selectedAssets }: { selectedAssets: NewImage[] }) => {
  const { t } = useTranslations();

  const hasUploadErrors = selectedAssets.some(
    (asset) =>
      asset.processStatus === PromiseStatus.Rejected ||
      asset.uploadStatus === PromiseStatus.Rejected,
  );

  if (hasUploadErrors) {
    return (
      <DetailImageContainer data-test={testIds.errorState}>
        <ErrorStatePageError height={64} width={64} />
        <ErrorStateText>{t('errors.uploadAsset.takeoverDetailFailed')}</ErrorStateText>
      </DetailImageContainer>
    );
  }

  if (selectedAssets.length === 0)
    return (
      <DetailImageContainer data-test={testIds.imageNone}>
        <EmptyStateImages height={64} width={64} />
        <CTA>{t('caption.selectToEdit')}</CTA>
      </DetailImageContainer>
    );

  if (selectedAssets.length === 1) {
    return selectedAssets[0].file.file ? (
      <SingleImageFile file={selectedAssets[0].file.file} />
    ) : (
      <StyledSingleImage
        $imgSrc={selectedAssets[0].asset?.url || ''}
        data-test={testIds.imageSingle}
      />
    );
  }

  return (
    <>
      <ImageStack>
        {selectedAssets.length > 2 ? (
          <StackableImage position={3} asset={selectedAssets[2]} />
        ) : null}
        <StackableImage position={2} asset={selectedAssets[1]} />
        <StackableImage position={1} asset={selectedAssets[0]} />
      </ImageStack>
      <ImageStackLbl>{t('caption.editingImages', { count: selectedAssets.length })}</ImageStackLbl>
    </>
  );
};
